import * as React from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n';
import { capitalizeFirstLetter, getBaseUrl } from '../../../utilities';
import ImagePlaceholder from '../../_helpers/Image/ImagePlaceholder';

const NEAR_BLACK = '#171717';
const DIVIDER_RED = 'rgba(227, 23, 62, 0.5)';
const MAX_CHILD_LINKS = 3;

const PLACEHOLDER = '/images/placeholder-100.png';
const PLACEHOLDER_2X = '/images/placeholder-100@2x.png';

interface FlyoutItem {
  category_id?: number | string;
  category_name?: string;
  url?: string | null;
  image?: string | null;
  children?: any[];
}

interface CategoryFlyoutProps {
  items: FlyoutItem[];
  onNavigate?: () => void;
  onDismiss?: () => void;
  dismissBelow?: number | null;
}

const CategoryFlyout = ({
  items,
  onNavigate,
  onDismiss,
  dismissBelow,
}: CategoryFlyoutProps) => (
  <Panel>
    <Grid>
      {items.map((item, i) => {
        const to = item.url ? getBaseUrl(item.url) : '/';
        const children = (item.children || []).filter(Boolean);
        const shown = children.slice(0, MAX_CHILD_LINKS);
        const hasMore = children.length > MAX_CHILD_LINKS;

        const parts: React.ReactNode[] = shown.map((c, j) => (
          <ChildLink
            key={`c-${c.category_id ?? j}`}
            to={getBaseUrl(c.url)}
            onClick={onNavigate}
          >
            {capitalizeFirstLetter(c.category_name || '')}
          </ChildLink>
        ));
        if (hasMore) {
          parts.push(<Ellipsis key="ellipsis">…</Ellipsis>);
          parts.push(
            <MoreLink key="more" to={to} onClick={onNavigate}>
              {__('Ďalšie')}
            </MoreLink>,
          );
        }

        return (
          <Card key={item.category_id ?? i}>
            <CardImageCell>
              <CardImage
                src={item.image || null}
                placeholder={PLACEHOLDER}
                retinaPlaceholder={PLACEHOLDER_2X}
                w={72}
                h={72}
                alt={capitalizeFirstLetter(item.category_name || '')}
              />
            </CardImageCell>
            <CardBody>
              <CardTitle to={to} onClick={onNavigate}>
                {capitalizeFirstLetter(item.category_name || '')}
              </CardTitle>
              {parts.length ? (
                <ChildList>
                  {parts.map((node, j) => (
                    <React.Fragment key={j}>
                      {/* The list is inline text flow (see ChildList); <wbr>
                          allows a line break between links (there is no other
                          whitespace between them). No divider before the
                          trailing "Ďalšie" link (the last part when hasMore). */}
                      {j > 0 ? <wbr /> : null}
                      {j > 0 && !(hasMore && j === parts.length - 1) ? (
                        <Divider aria-hidden="true" />
                      ) : null}
                      {node}
                    </React.Fragment>
                  ))}
                </ChildList>
              ) : null}
            </CardBody>
          </Card>
        );
      })}
    </Grid>
    <DeadZone
      onMouseMove={(e) => {
        if (dismissBelow == null || e.clientY > dismissBelow) {
          onDismiss?.();
        }
      }}
    />
  </Panel>
);

const Panel = styled.div`
  width: min(${rem(1160)}, calc(100vw - ${rem(300)}));
  min-height: 100%;
  background: #ebebeb;
  padding: ${rem(16)};
  /* Column layout so the dead zone below the grid can absorb the leftover
     panel space (see DeadZone). */
  display: flex;
  flex-direction: column;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${rem(15)};
`;

const DeadZone = styled.div`
  flex: 1 1 auto;
  margin: ${rem(32)} ${rem(-16)} ${rem(-16)};
`;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(24)};
  padding: ${rem(8)};
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid #e0e0e0;
  border-radius: ${rem(8)};
  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.05);
`;

const CardImageCell = styled.div`
  flex: 0 0 ${rem(72)};
  width: ${rem(72)};
  height: ${rem(72)};
`;

const CardImage = styled(ImagePlaceholder)`
  width: ${rem(72)};
  height: ${rem(72)};
`;

const CardBody = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`;

const CardTitle = styled(Link)`
  color: ${NEAR_BLACK};
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: ${rem(24)};
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: ${NEAR_BLACK};
    text-decoration: underline;
  }
`;

const ChildList = styled.div`
  line-height: ${rem(24)};
`;

const Divider = styled.span`
  display: inline-block;
  width: 0;
  height: ${rem(16)};
  border-left: ${rem(1)} solid ${DIVIDER_RED};
  margin: 0 ${rem(8)};
  vertical-align: ${rem(-2)};
`;

const childTextStyles = `
  font-size: ${rem(15)};
  line-height: ${rem(20)};
  color: ${NEAR_BLACK};
`;

const ChildLink = styled(Link)`
  ${childTextStyles};
  text-decoration: none;

  &:hover {
    color: ${NEAR_BLACK};
    text-decoration: underline;
  }
`;

const MoreLink = styled(Link)`
  ${childTextStyles};
  /* No divider before "Ďalšie", so the gap to the previous link is its own. */
  margin-left: ${rem(8)};
  text-decoration: underline;

  &:hover {
    color: ${NEAR_BLACK};
    text-decoration: underline;
  }
`;

const Ellipsis = styled.span`
  ${childTextStyles};
`;

export default CategoryFlyout;
